/**
 * Pay-day helpers: the day vocabulary the budget speaks.
 *
 * In `_shared/` because BOTH halves need it. The app renders a pay day and a
 * due day in the setup flow, and `kin-chat`'s `get_budget` has to say "due the
 * 5th" and work out which paycheck covers which bill — and those two must not
 * disagree about what day a stored value means. `src/lib/payDay.ts` is a
 * re-export shim so no app import site changed when this moved; same direction
 * as `_shared/billExpiry.ts`, which this file already depended on.
 *
 * ⚠️ Relative imports with a `.ts` extension, no path aliases, no React. Deno
 * resolves this file directly and cannot reach into `src/` or read Vite's `@/`
 * alias. `allowImportingTsExtensions` is on, so the extension is also fine on
 * the Vite side; `_shared/kinFeedRows.ts` already relies on that.
 *
 * `personal_budget_paychecks.pay_date` is a Postgres `date` column
 * (20260522032736_*.sql: `pay_date date`), so a pay day has to round-trip
 * through a real date — an integer day-of-month cannot be stored there without
 * a schema change. The select therefore offers ordinals and "Last day", and
 * these helpers convert between that choice and a date inside the budget's own
 * month.
 *
 * ⚠️ NEVER `new Date("YYYY-MM-DD")`. That parses as UTC, so every timezone west
 * of UTC reads the day back one short — the 15th renders as the 14th in
 * America/New_York. Every parse below splits the string and every construction
 * uses the local multi-argument `Date` constructor, which has no UTC step.
 */

import { splitISODate } from "./billExpiry.ts";

/** The "Last day" choice, as distinct from any fixed day number. */
export const LAST_DAY = "last" as const;
export type PayDay = number | typeof LAST_DAY;

export const WEEKDAY_LABELS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
] as const;

/**
 * Split an ISO date WITHOUT constructing a Date — see the UTC warning above.
 *
 * ⚠️ Re-exported from `./billExpiry.ts` rather than implemented twice. Two
 * implementations of "read an ISO date without a UTC shift" is how a bill and
 * its total end up disagreeing about which month it falls in.
 */
export { splitISODate };

/**
 * How many days `monthISO`'s month actually has.
 *
 * Day 0 of the FOLLOWING month is the last day of this one. `m` is 1-based and
 * the constructor's month argument is 0-based, so passing `m` already means
 * "next month" — the off-by-one is the mechanism, not a bug.
 */
export function lastDayOfMonth(monthISO: string): number {
  const parts = splitISODate(monthISO);
  if (!parts) return 31;
  return new Date(parts.y, parts.m, 0).getDate();
}

/**
 * The date to store for a chosen pay day, clamped into the month's real length.
 *
 * Clamping is why 29/30/31 are offered unconditionally: picking the 31st in a
 * 30-day month stores the 30th rather than rejecting the choice or rolling
 * into the next month.
 */
export function payDateForMonth(monthISO: string, day: PayDay): string | null {
  const parts = splitISODate(monthISO);
  if (!parts) return null;
  const last = lastDayOfMonth(monthISO);
  const resolved =
    day === LAST_DAY ? last : Math.min(Math.max(1, Math.trunc(day)), last);
  return `${parts.y}-${String(parts.m).padStart(2, "0")}-${String(resolved).padStart(2, "0")}`;
}

/**
 * The day a stored value represents, for reloading the select.
 *
 * Accepts the ISO date this module writes AND the free-text shapes the old
 * box allowed ("15", "15th", "Last day"), so a value saved before this change
 * loads into the select instead of appearing blank and being silently
 * overwritten on the next save.
 */
export function payDayFromStored(stored: string | null | undefined): PayDay | null {
  if (!stored) return null;
  const raw = String(stored).trim();
  if (raw === "") return null;

  const iso = splitISODate(raw);
  if (iso) return iso.d;

  if (/^last(\s*day)?$/i.test(raw)) return LAST_DAY;

  const ordinal = /^(\d{1,2})\s*(?:st|nd|rd|th)?$/i.exec(raw);
  if (ordinal) {
    const d = Number(ordinal[1]);
    if (d >= 1 && d <= 31) return d;
  }
  return null;
}

/** 1 -> "1st", 2 -> "2nd", 11 -> "11th", 21 -> "21st". */
export function ordinalLabel(day: number): string {
  const rem100 = day % 100;
  if (rem100 >= 11 && rem100 <= 13) return `${day}th`;
  switch (day % 10) {
    case 1:
      return `${day}st`;
    case 2:
      return `${day}nd`;
    case 3:
      return `${day}rd`;
    default:
      return `${day}th`;
  }
}

/**
 * Display text for a stored pay date, e.g. "15th".
 *
 * Note a deliberate ambiguity: on a 31-day month the 31st and "Last day" store
 * the same date and both read back as "31st". The ordinal wins because it is
 * the more specific of the two, and because a user who picked "Last day" is
 * not misled by seeing the day it resolved to.
 */
export function payDayLabel(stored: string | null | undefined): string {
  const day = payDayFromStored(stored);
  if (day === null) return "";
  if (day === LAST_DAY) return "Last day";
  return ordinalLabel(day);
}

/**
 * Carry a pay date into another month: keep the DAY, rebuild the date.
 *
 * Copying the stored string verbatim is the bug this exists to prevent — Jan
 * 31 carried into February is not a date, and a 30th carried into February
 * would have to clamp anyway.
 */
export function carryPayDateToMonth(
  stored: string | null | undefined,
  targetMonthISO: string,
): string | null {
  const day = payDayFromStored(stored);
  if (day === null) return null;
  return payDateForMonth(targetMonthISO, day);
}

/**
 * Weekly pay: store the first occurrence of `weekday` in the month.
 *
 * A weekday is not a day-of-month, but the column is a date, so the weekday
 * has to be carried by one. The first occurrence is recoverable with
 * `weekdayFromStored`, which is what reloads the select.
 */
export function firstWeekdayDateForMonth(
  monthISO: string,
  weekday: number,
): string | null {
  const parts = splitISODate(monthISO);
  if (!parts) return null;
  const firstDow = new Date(parts.y, parts.m - 1, 1).getDay();
  const day = 1 + ((weekday - firstDow + 7) % 7);
  return payDateForMonth(monthISO, day);
}

/** The weekday a stored date falls on, for reloading the weekly select. */
export function weekdayFromStored(stored: string | null | undefined): number | null {
  if (!stored) return null;
  const iso = splitISODate(String(stored));
  if (!iso) return null;
  return new Date(iso.y, iso.m - 1, iso.d).getDay();
}

/**
 * `personal_budget_bills.due_day`'s sentinel for "the last day of the month".
 *
 * 32 rather than 0 because 0 is falsy: this feature's whole job is telling "no
 * due date" (null) apart from a real due day, and every `if (bill.due_day)`
 * would fold a last-day bill into the no-due-date group. 32 also sorts after
 * every real day, which is the correct order.
 */
export const DUE_DAY_LAST = 32;

/** A stored `due_day` as the PayDay the selects speak. */
export function payDayFromDueDay(dueDay: number | null | undefined): PayDay | null {
  if (dueDay === null || dueDay === undefined || !Number.isFinite(dueDay)) return null;
  if (dueDay >= DUE_DAY_LAST) return LAST_DAY;
  if (dueDay < 1) return null;
  return Math.trunc(dueDay);
}

/** A PayDay back into the integer the column stores. */
export function dueDayFromPayDay(day: PayDay | null): number | null {
  if (day === null) return null;
  if (day === LAST_DAY) return DUE_DAY_LAST;
  if (!Number.isFinite(day) || day < 1) return null;
  return Math.min(Math.trunc(day), 31);
}

/** "5th", "Last day", or "" — what a bill row shows next to its amount. */
export function dueDayLabel(dueDay: number | null | undefined): string {
  const day = payDayFromDueDay(dueDay);
  if (day === null) return "";
  return day === LAST_DAY ? "Last day" : ordinalLabel(day);
}
